/**
 * Re-usable JSON-LD schema builders for Schedule Guru.
 *
 * Reference: https://developers.google.com/search/docs/appearance/structured-data
 */

const SITE_URL = 'https://scheduleguru.ws';
const ORG_NAME = 'Schedule Guru';
const ORG_LOGO = `${SITE_URL}/logo-transparent.png`;
const SHARE_IMAGE = `${SITE_URL}/og-image.png`;
const SLOGAN = 'Your League. One Platform.';
const PLATFORM_DESCRIPTION =
  'All-in-one sports league and tournament management software. Generate schedules, manage teams and rosters, ' +
  'track standings and player statistics, organize playoffs, and assign officials, all in one platform.';

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: ORG_NAME,
    url: SITE_URL,
    logo: ORG_LOGO,
    slogan: SLOGAN,
    sameAs: [],
    description: PLATFORM_DESCRIPTION,
  };
}

export function webSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: ORG_NAME,
    url: SITE_URL,
    description: PLATFORM_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * SoftwareApplication schema — positions Schedule Guru as all-in-one league
 * management software; the free round robin generator stays in featureList.
 */
export function softwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Schedule Guru',
    alternateName: `Schedule Guru — ${SLOGAN}`,
    operatingSystem: 'Web',
    applicationCategory: 'SportsApplication',
    applicationSubCategory: 'Sports League Management Software',
    description: PLATFORM_DESCRIPTION,
    url: SITE_URL,
    image: SHARE_IMAGE,
    offers: [
      {
        '@type': 'Offer',
        name: 'Free',
        price: '0',
        priceCurrency: 'USD',
        description: 'Round robin scheduling for up to 8 teams, PDF/Excel exports, shareable links.',
      },
      {
        '@type': 'Offer',
        name: 'Pro Monthly',
        price: '24.00',
        priceCurrency: 'USD',
        description:
          'Unlimited teams, every bracket format (single/double elimination, pool play, consolation, hybrid), ' +
          'live multi-sport stat tracking, AI schedule + box-score import, season analytics, roster manager, ' +
          'branded exports, multi-admin collaboration, priority support.',
      },
      {
        '@type': 'Offer',
        name: 'Pro Annual',
        price: '199.00',
        priceCurrency: 'USD',
        description: 'All Pro features billed yearly (~31% savings vs monthly, ~$16.58 USD/mo).',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '247',
      bestRating: '5',
      worstRating: '1',
    },
    featureList: [
      'Schedule generator for leagues and tournaments (free round robin)',
      'Single & double elimination, pool play and consolation brackets',
      'Team and roster management',
      'Player profile pages',
      'Live standings with tiebreakers',
      'Playoff brackets that advance automatically',
      'Live stat tracking and AI box-score scanning',
      'Season leaderboards and player statistics',
      'Officials scheduling and payments',
      'PDF, Excel, iCal export and shareable public pages',
      'Multi-admin collaboration',
    ],
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export function blogPostingSchema({
  title,
  description,
  slug,
  date,
  modifiedDate,
  author = 'Schedule Guru Team',
  image,
  wordCount,
  category,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    image: image
      ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
      : ORG_LOGO,
    author: { '@type': 'Organization', name: author, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: ORG_NAME,
      logo: { '@type': 'ImageObject', url: ORG_LOGO },
    },
    datePublished: date,
    dateModified: modifiedDate || date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${slug}` },
    ...(wordCount && { wordCount }),
    ...(category && { articleSection: category }),
  };
}

/**
 * FAQPage schema — only attach to posts that include a Q&A-styled section.
 * `faqs` is an array of `{question, answer}` objects.
 */
export function faqPageSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}
